/**
 * tourKeys.js
 * Centralized registry of all GuidedTour localStorage keys and a utility to
 * clear them during demo resets.
 *
 * Each entry maps a DemoResetService reset_type to the localStorage key(s)
 * for the tours on that feature's pages.
 *
 * Key patterns:
 * - Static keys (e.g. 'cfa_results_tour_seen') are matched exactly.
 * - Prefix keys (e.g. 'launchpad_tour_shown_v1_') match any key that starts
 *   with the prefix, covering per-user or per-entity suffixes.
 */

const TOUR_KEY_REGISTRY = {
  onboarding: {
    prefixes: ['launchpad_tour_shown_v1_', 'succession_quickstart_welcome_dismissed_'],
    exact: ['succession_home_tour_completed', 'succession_home_tour_shown_v1'],
  },
  valuation_studio: {
    prefixes: ['mlv_tour_shown_v1_'],
    exact: [],
  },
  plan_builder: {
    prefixes: ['plan_builder_tour_shown_', 'plan_completion_tour_shown_'],
    exact: [],
  },
  value_builder: {
    prefixes: ['value_acceleration_tour_shown_v1_'],
    exact: [],
  },
  cash_flows: {
    prefixes: [],
    exact: ['cfa_results_tour_seen'],
  },
  deal_room: {
    prefixes: ['deal_teaser_tour_shown_v1_'],
    exact: [],
  },
};

// Student dashboard is separate because it doesn't map to a DemoResetService type,
// but should still be clearable via "all"
const STUDENT_TOUR_KEYS = {
  prefixes: [],
  exact: ['student_dashboard_tour_completed'],
};

/**
 * clearTourKeys - Remove tour localStorage flags for a given reset type.
 *
 * @param {string} resetType - One of the DemoResetService reset types, or 'all'.
 *   Passing 'all' clears every registered tour key.
 *   Passing a specific type (e.g. 'valuation_studio') clears only those tours.
 */
export function clearTourKeys(resetType) {
  const groups = [];

  if (resetType === 'all') {
    Object.values(TOUR_KEY_REGISTRY).forEach((g) => groups.push(g));
    groups.push(STUDENT_TOUR_KEYS);
  } else if (TOUR_KEY_REGISTRY[resetType]) {
    groups.push(TOUR_KEY_REGISTRY[resetType]);
  }

  if (groups.length === 0) return;

  // Collect all prefixes and exact keys
  const prefixes = [];
  const exactKeys = [];
  groups.forEach((g) => {
    prefixes.push(...g.prefixes);
    exactKeys.push(...g.exact);
  });

  try {
    // Remove exact matches
    exactKeys.forEach((key) => {
      localStorage.removeItem(key);
    });

    // Remove prefix matches by scanning localStorage
    if (prefixes.length > 0) {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && prefixes.some((p) => key.startsWith(p))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    }
  } catch (_) {
    // localStorage unavailable (private browsing, etc.) — silently skip
  }
}

export default clearTourKeys;
